import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const UpgradeModal = ({ open, onClose, currentTier, reason }) => {
  const [loading, setLoading] = useState(false);

  const plans = [
    {
      tier: 'premium',
      name: 'Premium',
      price: 3,
      badge: 'Popular',
      features: [
        '10 service listings',
        '8 photos + 1×30s video per listing',
        'Quote builder (20 line items)',
        'Reply to reviews',
        'Pin 1 review',
        'Priority verification',
      ],
    },
    {
      tier: 'pro',
      name: 'Pro',
      price: 5,
      badge: 'Best Value',
      features: [
        '25 service listings',
        '12 photos + 2×30s videos',
        'Unlimited quote items',
        'Reply to reviews',
        'Pin 3 reviews',
        'Sponsored placements',
        'Team management (3 seats)',
      ],
    },
  ];

  const handleUpgrade = async (tier) => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const response = await axios.post(`${API}/subscriptions/checkout`, null, {
        params: {
          plan_code: tier,
          origin_url: origin
        }
      });

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">Upgrade Your Plan</DialogTitle>
          <DialogDescription>
            {reason || 'Unlock more features and grow your business'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Current Plan */}
          <Card className="bg-muted/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Current Plan</p>
                  <p className="text-lg font-bold capitalize">{currentTier}</p>
                </div>
                <Badge variant="secondary">Active</Badge>
              </div>
            </CardContent>
          </Card>

          {/* Available Plans */}
          <div className="grid md:grid-cols-2 gap-6">
            {plans.map((plan) => (
              <Card
                key={plan.tier}
                className={`relative ${
                  plan.tier === 'premium' ? 'border-primary border-2 shadow-lg' : ''
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-white">{plan.badge}</Badge>
                  </div>
                )}

                <CardContent className="p-6 space-y-4">
                  <div className="text-center pt-2">
                    <h3 className="text-2xl font-bold">{plan.name}</h3>
                    <div className="mt-3">
                      <span className="text-4xl font-bold">${plan.price}</span>
                      <span className="text-muted-foreground">/30 days</span>
                    </div>
                  </div>

                  <Separator />

                  <ul className="space-y-2">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => handleUpgrade(plan.tier)}
                    disabled={loading || currentTier === plan.tier}
                    className={`w-full rounded-xl ${
                      plan.tier === 'premium'
                        ? 'bg-primary hover:bg-primary-hover'
                        : 'bg-[hsl(var(--accent-yellow))] text-[hsl(var(--accent-yellow-foreground))] hover:opacity-90'
                    }`}
                    data-testid={`upgrade-to-${plan.tier}`}
                  >
                    {loading ? 'Processing...' : currentTier === plan.tier ? 'Current Plan' : `Upgrade to ${plan.name}`}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Benefits */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-6">
              <h4 className="font-semibold mb-3">✨ Why Upgrade?</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Get more listings to showcase your services</li>
                <li>• Build trust by replying to customer reviews</li>
                <li>• Stand out with pinned reviews and priority placement</li>
                <li>• Professional quote builder with unlimited items</li>
                <li>• Cancel anytime, no lock-in contracts</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};
